import { z } from 'zod';
export const workforceDirectoryCapability = 'workforce.directory.v1';
export const workforceEmployeeProfilesCapability = 'workforce.employee-profiles.v1';
export const workforceMembershipIdSchema = z.string().uuid();
export const workforceCommandIdSchema = z.string().uuid();
export const workforceEmploymentStatusSchema = z.enum([
    'active', 'inactive', 'leave', 'terminated',
]);
export const workforceEmploymentTypeSchema = z.enum([
    'hourly', 'salary', 'contractor',
]);
const workforceNullableText = (maximum) => z.string().trim().max(maximum).nullable();
const workforceDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day ?? 0));
    return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day;
}, 'Date must be a real calendar date in YYYY-MM-DD form.');
export const workforceEmployeeProfileInputSchema = z.strictObject({
    employeeNumber: workforceNullableText(80),
    jobTitle: workforceNullableText(160),
    employmentStatus: workforceEmploymentStatusSchema,
    employmentType: workforceEmploymentTypeSchema.nullable(),
    companyCode: z.string().trim().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(80).nullable(),
    companyLabel: workforceNullableText(160),
    department: workforceNullableText(120),
    roleLabels: z.array(z.string().trim().min(1).max(80)).max(12),
    managerMembershipId: workforceMembershipIdSchema.nullable(),
    startDate: workforceDateSchema.nullable(),
    terminationDate: workforceDateSchema.nullable(),
    birthdayLabel: workforceNullableText(80),
    dateOfBirth: workforceDateSchema.nullable(),
    personalEmail: z.string().trim().email().max(320).nullable(),
    secondaryPhone: workforceNullableText(40),
    addressLine1: workforceNullableText(240),
    addressLine2: workforceNullableText(240),
    city: workforceNullableText(120),
    state: workforceNullableText(80),
    postalCode: workforceNullableText(24),
    emergencyContactName: workforceNullableText(160),
    emergencyContactRelationship: workforceNullableText(120),
    emergencyContactPhone: workforceNullableText(40),
    emergencyContactAlternatePhone: workforceNullableText(40),
    emergencyContactNotes: workforceNullableText(2_000),
    employmentNotes: workforceNullableText(12_000),
    customerProfileEnabled: z.boolean(),
    customerProfileTitle: workforceNullableText(160),
    customerProfileBio: workforceNullableText(1_200),
    identityColor: z.string().regex(/^#[0-9A-F]{6}$/),
});
export const workforceEmployeeProfileSchema = workforceEmployeeProfileInputSchema.extend({
    tenantId: z.string().uuid(),
    membershipId: workforceMembershipIdSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: workforceMembershipIdSchema,
    updatedByMembershipId: workforceMembershipIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const workforceEmployeeProfileVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    membershipId: workforceMembershipIdSchema,
    revision: z.number().int().positive(),
    operation: z.enum(['upsert', 'archive', 'restore']),
    profile: workforceEmployeeProfileInputSchema,
    archivedAfter: z.boolean(),
    reason: z.string().trim().min(5).max(500),
    actorMembershipId: workforceMembershipIdSchema,
    createdAt: z.string().datetime({ offset: true }),
});
export const workforceDirectoryEntrySchema = z.strictObject({
    membershipId: workforceMembershipIdSchema,
    displayName: z.string().trim().min(1).max(240),
    profileConfigured: z.boolean(),
    profileArchived: z.boolean(),
    profileRevision: z.number().int().positive().nullable(),
    jobTitle: workforceNullableText(160),
    companyCode: workforceNullableText(80),
    companyLabel: workforceNullableText(160),
    department: workforceNullableText(120),
    roleLabels: z.array(z.string().trim().min(1).max(80)).max(12),
    managerMembershipId: workforceMembershipIdSchema.nullable(),
    managerDisplayName: workforceNullableText(240),
    startDate: workforceDateSchema.nullable(),
    birthdayLabel: workforceNullableText(80),
    customerProfileEnabled: z.boolean(),
    customerProfileTitle: workforceNullableText(160),
    customerProfileBio: workforceNullableText(1_200),
    identityColor: z.string().regex(/^#[0-9A-F]{6}$/),
});
export const workforceDirectoryQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    department: z.string().trim().max(120).default(''),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().min(0).default(0),
});
export const workforceUpsertProfileCommandSchema = z.strictObject({
    kind: z.literal('upsert'),
    commandId: workforceCommandIdSchema,
    expectedRevision: z.number().int().positive().nullable(),
    profile: workforceEmployeeProfileInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const workforceProfileStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: workforceCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const workforceProfileCommandSchema = z.discriminatedUnion('kind', [
    workforceUpsertProfileCommandSchema,
    workforceProfileStateCommandSchema,
]);
const workforceCommandMetadataSchema = z.strictObject({
    commandId: workforceCommandIdSchema,
    idempotent: z.boolean(),
});
export const workforceDirectoryResponseSchema = z.strictObject({
    data: z.array(workforceDirectoryEntrySchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const workforceEmployeeProfileResponseSchema = z.strictObject({
    data: workforceEmployeeProfileSchema.nullable(),
    command: workforceCommandMetadataSchema.optional(),
});
export const workforceEmployeeProfileVersionsResponseSchema = z.strictObject({
    data: z.array(workforceEmployeeProfileVersionSchema),
});
export const workforceIdentityColorPalette = Object.freeze([
    '#00796B', '#D43D00', '#1565C0', '#9A6700', '#6A1B9A', '#2E7D32',
    '#C62828', '#1F2937', '#AD1457', '#6D4C41', '#0E7490', '#6B7280',
]);
export function workforceIdentityColorForMembership(membershipId) {
    let hash = 2166136261;
    for (const character of membershipId) {
        hash ^= character.charCodeAt(0);
        hash = Math.imul(hash, 16777619);
    }
    return workforceIdentityColorPalette[Math.abs(hash) % workforceIdentityColorPalette.length] ?? '#6B7280';
}
export function createWorkforceEmployeeProfileDefaults(membershipId) {
    return workforceEmployeeProfileInputSchema.parse({
        employeeNumber: null,
        jobTitle: null,
        employmentStatus: 'active',
        employmentType: null,
        companyCode: null,
        companyLabel: null,
        department: null,
        roleLabels: [],
        managerMembershipId: null,
        startDate: null,
        terminationDate: null,
        birthdayLabel: null,
        dateOfBirth: null,
        personalEmail: null,
        secondaryPhone: null,
        addressLine1: null,
        addressLine2: null,
        city: null,
        state: null,
        postalCode: null,
        emergencyContactName: null,
        emergencyContactRelationship: null,
        emergencyContactPhone: null,
        emergencyContactAlternatePhone: null,
        emergencyContactNotes: null,
        employmentNotes: null,
        customerProfileEnabled: false,
        customerProfileTitle: null,
        customerProfileBio: null,
        identityColor: workforceIdentityColorForMembership(membershipId),
    });
}
//# sourceMappingURL=workforce-profile.contract.js.map